import breadcrumb from '@opten/breadcrumb';
import factory from '@opten/compiler/factory';
import { Component } from '@opten/compiler/metadata/component';
import addClass from '@opten/dom/add-class';
import hasClass from '@opten/dom/has-class';
import removeClass from '@opten/dom/remove-class';
import FormField from '@opten/forms/form-field';
import mapExtended from '@opten/map-extended';
import { menuTrigger } from '@opten/menu';
import photoswipeGallery from '@opten/photoswipe-gallery';
import iframePlayer from '@opten/player';
import select from '@opten/select';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Anchor } from './components/anchor';
import { CenterImageComponent } from './components/center-image';
import { CollapseComponent } from './components/collapse';
import { ContextSliderComponent } from './components/context-slider';
import { cookieConsent } from './components/cookie-consent';
import { DelegateEvents } from './components/delegate-events';
import { FormComponent } from './components/form';
import { FormExtended } from './components/form-extended';
import { ImageSlider } from './components/image-slider';
import { InputFieldsFilled } from './components/input-fields-filled';
import { MenuButtonComponent } from './components/menu-button';
import { MenuCollapseComponent } from './components/menu-collapse';
import { MenuMultilevelComponent } from './components/menu-multilevel';
import { MixitupComponent, MixitupSearchComponent } from './components/mixitup-filter';
import { ModalComponent } from './components/modal';
import { NewsSliderComponent } from './components/news-slider';
import { PasswordVisibilityToggle } from './components/password-visibility-toggle';
import { ProductFilterSlider } from './components/product-filter-slider';
import { ProductImageSlider } from './components/product-image-slider';
import { ProductSlider } from './components/product-slider';
import { SearchCollapseComponent } from './components/search-collapse';
import { SidebarComponent } from './components/sidebar';
import { SliderComponent } from './components/slider';
import { StickyBasketComponent } from './components/sticky-basket';
import { TextCollapse } from './components/text-collapse';
import { TilesDropdownCollapseComponent } from './components/tiles-dropdown-collapse';
import { VideoBackgroundComponent } from './components/video-background';
import './ggwmain.scss';
import { CustomerAreaAuth } from './shop/account/customer-area';
import { ForgotPasswordComponent } from './shop/account/password-forgot';
import { ProfileComponent } from './shop/account/profile';
import { RequestRoles } from './shop/account/request-roles';
import { ShopAccountComponent } from './shop/account/shop-account';
import { SignInComponent } from './shop/account/sign-in';
import { SignOutComponent } from './shop/account/sign-out';
import { SignUpComponent } from './shop/account/sign-up';
import { SignUpNewComponent } from './shop/account/sign-up-new';
import { WantsToBeGuestComponent } from './shop/account/wants-to-be-guest';
import { BasketComponent } from './shop/basket/basket';
import { BasketQuantityComponent } from './shop/basket/basket-quantity';
import { InputCounterComponent } from './shop/basket/input-counter';
import { ProductComponent } from './shop/basket/product';
import { LastOrderedProducts } from './shop/order/last-ordered-products';
import { OrdersComponent } from './shop/order/orders';
import { OrdersMobileNav } from './shop/order/orders-mobile-nav';
import { ProductDetail } from './shop/shop/product-detail';
import { ShopCategoryComponent } from './shop/shop/shop-category';
import { ShopContext } from './shop/shop/shop-context';
import { MOBILE_WIDTH } from './utilities/detection';
import { Loader } from '@googlemaps/js-api-loader';
import { CookiebotRenewComponent } from './components/cookie-renew';

@Component({ className: 'js-breadcrumb' })
class Breadcrumb {
	constructor(private element: HTMLElement) {
		breadcrumb(element, {
			breakpoints: {
				maxWidth: MOBILE_WIDTH
			},
			initializedClassName: 'is--initialized'
		});
	}
}

@Component({ className: 'js-cookie-consent' })
class CookieConsent {
	constructor(private element: HTMLElement) {
		const header = document.querySelector('.navigation') as HTMLElement;
		const shopSearch = document.querySelector('.c-shop-search') as HTMLElement;
		const salesBanner = document.querySelector('.c-sales-banner') as HTMLElement;
		const sideNav = document.querySelector('.c-sidenav') as HTMLElement;
		const elements = [header, shopSearch, sideNav, salesBanner];

		cookieConsent(
			elements.filter(o => o),
			{
				showAfter: 600,
				cookie: {
					expirationInDays: 365,
					secure: true
				}
			}
		);
	}
}

@Component({ className: 'js-map-extended' })
class MapExtended {
	constructor(private element: HTMLElement) {
		const showMapMarkerClasses = element.getAttribute('data-show-marker-classes');
		const showMapMarkerClassesList = showMapMarkerClasses.length > 0 ? showMapMarkerClasses.split(',') : [];
		const mapMarkerInformations = Array.from(element.getElementsByClassName('c-map-extended__information'));
		let mapMarkerClasses = [];
		if (mapMarkerInformations != null) {
			mapMarkerInformations.forEach(e => {
				mapMarkerClasses.push(e.getAttribute('data-marker-class'));
			});
			mapMarkerClasses = mapMarkerClasses.filter((v, i, a) => a.indexOf(v) === i);
		}
		const mapStylesAll = [
			{
				featureType: 'administrative',
				elementType: 'labels.text.fill',
				stylers: [
					{
						color: '#3c3c3c'
					}
				]
			},
			{
				featureType: 'administrative.country',
				elementType: 'geometry.fill',
				stylers: [
					{
						visibility: 'off'
					}
				]
			},
			{
				featureType: 'landscape',
				elementType: 'all',
				stylers: [
					{
						color: '#f5f5f5'
					}
				]
			},
			{
				featureType: 'poi',
				elementType: 'all',
				stylers: [
					{
						visibility: 'off'
					}
				]
			},
			{
				featureType: 'road',
				elementType: 'all',
				stylers: [
					{
						saturation: -100
					},
					{
						lightness: 45
					}
				]
			},
			{
				featureType: 'road.highway',
				elementType: 'all',
				stylers: [
					{
						visibility: 'simplified'
					}
				]
			},
			{
				featureType: 'road.arterial',
				elementType: 'labels.icon',
				stylers: [
					{
						visibility: 'off'
					}
				]
			},
			{
				featureType: 'transit',
				elementType: 'all',
				stylers: [
					{
						visibility: 'off'
					}
				]
			},
			{
				featureType: 'water',
				elementType: 'all',
				stylers: [
					{
						color: '#46aacd'
					},
					{
						visibility: 'on'
					}
				]
			},
			{
				featureType: 'water',
				elementType: 'geometry.fill',
				stylers: [
					{
						gamma: 1
					},
					{
						visibility: 'on'
					},
					{
						color: '#46aacd'
					}
				]
			},
			{
				featureType: 'water',
				elementType: 'geometry.stroke',
				stylers: [
					{
						visibility: 'off'
					}
				]
			},
			{
				featureType: 'water',
				elementType: 'labels.text',
				stylers: [
					{
						visibility: 'off'
					}
				]
			}
		];
		mapExtended(element, {
			showMapMarkerClasses: showMapMarkerClassesList,
			mapMarkerClasses,
			mapStyles: mapStylesAll,
			hasMapMarkerClassesButtonIfMultiple: true,
			styledMapName: 'Gehrig Group'
		});
	}
}

@Component({ className: 'js-menu-trigger' })
class MenuTrigger {
	constructor(private element: HTMLElement) {
		menuTrigger(element);
	}
}

@Component({ className: 'js-player' })
export class IFramePlayer {
	constructor(private element: HTMLElement) {
		iframePlayer(element);
	}
}

@Component({ className: 'js-photoswipe-gallery-element', observe: true })
class PhotoswipeGalleryComponent {
	constructor(element: HTMLElement) {
		photoswipeGallery(element);
	}
}

@Component({ className: 'js-select' })
class Select {
	constructor(private element: HTMLSelectElement) {
		select(element);
	}
}

@Component({ className: 'js-header' })
class Header {
	constructor(private element: HTMLElement) {
		if (hasClass(this.element, 'is--transparent')) {
			window.addEventListener('scroll', () => {
				if (window.scrollY > 0) {
					addClass(this.element, 'is--stuck');
				} else {
					removeClass(this.element, 'is--stuck');
				}
			});
		}
	}
}

window.isPolyfillLoaded = new BehaviorSubject<boolean>(false);
const loader = new Loader({
	apiKey: 'AIzaSyCVldQWL-f8RYjCuVIdMaP0JJv5-TKByDI',
	version: 'weekly'
});
loader
	.importLibrary('maps')
	.then(
		() => {
			console.log('google maps loaded successfuly');
			factory([MapExtended]);
		},
		() => {
			console.log('google maps loading rejected');
		}
	)
	.catch(error => {
		console.log('failed import google maps library', error);
	});

document.addEventListener('DOMContentLoaded', () => {
	factory([
		Header,
		BasketQuantityComponent,
		ShopContext,
		LastOrderedProducts,
		MenuCollapseComponent,
		SearchCollapseComponent,
		MenuButtonComponent,
		MenuMultilevelComponent,
		Anchor,
		SliderComponent,
		NewsSliderComponent,
		CenterImageComponent,
		SidebarComponent,
		FormField,
		InputFieldsFilled,
		Breadcrumb,
		MenuTrigger,
		IFramePlayer,
		DelegateEvents,
		SignInComponent,
		SignUpComponent,
		SignOutComponent,
		SignUpNewComponent,
		ShopAccountComponent,
		ShopCategoryComponent,
		BasketComponent,
		InputCounterComponent,
		ProductComponent,
		ForgotPasswordComponent,
		ProfileComponent,
		OrdersComponent,
		Select,
		FormComponent,
		PhotoswipeGalleryComponent,
		CookieConsent,
		PasswordVisibilityToggle,
		ModalComponent,
		StickyBasketComponent,
		WantsToBeGuestComponent,
		ContextSliderComponent,
		CustomerAreaAuth,
		RequestRoles,
		ProductSlider,
		ProductFilterSlider,
		ProductImageSlider,
		ProductDetail,
		ImageSlider,
		VideoBackgroundComponent,
		CollapseComponent,
		FormExtended,
		OrdersMobileNav,
		TextCollapse,
		TilesDropdownCollapseComponent,
		MixitupComponent,
		MixitupSearchComponent,
		CookiebotRenewComponent
	])(document.body);

	if (isMobile.apple.device || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
		addClass(document.body, 'is-ios');
	}

	window.isPolyfillLoaded.next(true);

	fromEvent(document, 'lazyloaded')
		.pipe(debounceTime(500))
		.subscribe(() => {
			requestAnimationFrame(() => {
				const e = document.createElement('DIV');
				e.style.position = 'fixed';
				e.style.top = '0';
				e.style.left = '0';
				e.style.right = '0';
				e.style.bottom = '0';
				e.style.backgroundColor = '#80808001';
				e.style.pointerEvents = 'none';
				e.style.zIndex = '9999999';
				document.body.appendChild(e);
				requestAnimationFrame(() => e.remove());
			});
		});
});
